import { ButtonProps } from "@mui/material/Button/Button";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "../../../components/Dialog";
import { Fields } from "../../../components/Forms/Fields";
import { Infos } from "../../../components/Forms/Infos";
import { useToast } from "../../../hooks/useToast";
import { useStyles } from "../styles";
import { CreateFormData, Form } from "../../../types/forms";
import { Colors } from "../../../components/Forms/Colors";
import {
  FIELDS_TYPE_FORMS,
  FORM_FIELDS_PERSON,
} from "../../../utils/constants";
import { Review } from "../../../components/Forms/Review";
import { map } from "lodash";
import { useMutation } from "react-query";
import { putForm } from "../../../services/forms";
import { queryClient } from "../../../services/queryClient";

type EditFormProps = {
  data: Form;
  open: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({
  typeForm: Yup.string().required("Tipo inválida"),
  nameForm: Yup.string().required("Nome obrigatório"),
  fontColorForm: Yup.string().required("Cor da fonte obrigatório"),
  bgColorForm: Yup.string().required("Cor de fundo obrigatório"),
  fields: Yup.array()
    .when("typeForm", {
      is: (typeForm) => {
        return typeForm ? typeForm === "ADD_CLIENTS" : false;
      },
      then: Yup.array().of(
        Yup.object().shape({
          active: Yup.string(),
          label: Yup.string().required("Pergunta obrigatório"),
          required: Yup.boolean(),
          key: Yup.object().shape({
            value: Yup.string().required("O identificador é obrigatório"),
            label: Yup.string(),
          }),
          inputType: Yup.object()
            .shape({
              value: Yup.string(),
              label: Yup.string(),
            })
            .required("Tipo do campo obrigatório"),
          optionSelect: Yup.array().nullable().of(Yup.string()),
        })
      ),
      otherwise: Yup.array().of(
        Yup.object().shape({
          active: Yup.string(),
          label: Yup.string().required("Pergunta obrigatório"),
          required: Yup.boolean(),
          key: Yup.object().shape({
            value: Yup.string(),
            label: Yup.string(),
          }),
          inputType: Yup.object()
            .shape({
              value: Yup.string(),
              label: Yup.string(),
            })
            .required("Tipo do campo obrigatório"),
          optionSelect: Yup.array().nullable().of(Yup.string()),
        })
      ),
    })
    .when("typeForm", {
      is: (typeForm) => {
        return typeForm ? typeForm === "CUSTOMER_LIKES" : false;
      },
      then: (schema) => {
        return schema.max(6, "Formulário atingiu o máximo de campos");
      },
    })
    .min(1, "Formulário precisa de um campo válido")
    .required("Campo obrigatório"),
});

export function EditForm({ onClose, open, data }: EditFormProps) {
  const formikRef = useRef<FormikProps<CreateFormData>>(null);

  const toast = useToast();
  const styles = useStyles();
  const [activeStep, setActiveStep] = useState<number>(0);

  const updateForm = useMutation(
    async (data: any) => {
      const response = await putForm(data);
      return response.message;
    },
    {
      onSuccess: (message) => {
        toast.show(message, {});
        queryClient.invalidateQueries(["forms"]);
        handleClose();
      },
      onError: () => {
        toast.show("Erro ao atualizar o formulário", {});
      },
    }
  );

  const steps = useMemo(() => {
    return ["Informações", "Campos", "Cores", "Resumo"];
  }, []);

  const fieldsForm = useMemo(() => {
    return map(data?.fields, (field) => {
      return {
        ...field,
        disabled:
          data?.typeForm === "CUSTOMER_LIKES" &&
          String(field.key) === "documento",
        key: FORM_FIELDS_PERSON.find(
          (type) => type.value === String(field.key)
        ),
        inputType: FIELDS_TYPE_FORMS.find(
          (type) => type.value === String(field.inputType)
        ),
      };
    });
  }, [data?.fields, data?.typeForm]);

  const handleUpdate = useCallback(
    async (form: CreateFormData) => {
      const updateFormData = {
        ...form,
        id_formulario: data.id_formulario,
        ativo: data.ativo,
        fields: map(form.fields, (field) => ({
          id_form_campo: field.id_form_campo,
          active: field.active ? 1 : 0,
          inputType:
            typeof field.inputType === "string"
              ? field.inputType
              : field.inputType.value,
          key: typeof field.key === "string" ? field.key : field.key?.value,
          label: field.label,
          optionSelect:
            Array.isArray(field.optionSelect) && field.optionSelect.length > 0
              ? JSON.stringify(field.optionSelect)
              : null,
          required: field.required ? 1 : 0,
          idFather: field.idFather,
          isFieldSon: field.isFieldSon ? 1 : 0,
          fieldFather: field.fieldFather
            ? JSON.stringify(field.fieldFather)
            : null,
          ...(field.guid && { guid: field.guid }),
          ...(field.guidFather && { guidFather: field.guidFather }),
        })),
      };

      await updateForm.mutateAsync(updateFormData);
    },
    [updateForm, data?.ativo, data?.id_formulario]
  );

  const handleClose = useCallback(() => {
    setActiveStep(0);
    onClose();
  }, [onClose, setActiveStep]);

  const handleValidateStep = useCallback((): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      try {
        switch (activeStep) {
          case 0:
            await formikRef.current?.validateField("typeForm");
            await formikRef.current?.validateField("nameForm");
            break;
          case 1:
            await formikRef.current?.validateField("fields");
            break;
        }

        resolve(formikRef.current?.isValid);
      } catch (err) {
        reject(false);
      }
    });
  }, [activeStep, formikRef]);

  const handleGoBack = useCallback(() => {
    formikRef.current?.setErrors({});
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleGoNext = useCallback(async () => {
    try {
      const isValid = await handleValidateStep();

      if (isValid) {
        setActiveStep((prev) => prev + 1);
      }
    } catch (err) {
      console.log(err);
    }
  }, [handleValidateStep]);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Voltar",
        onClick: () => handleGoBack(),
        disabled: activeStep === 0 || updateForm.isLoading,
      },
      {
        disabled: updateForm.isLoading,
        children: updateForm.isLoading
          ? "Enviado solicitação..."
          : activeStep === 3
          ? "Atualizar"
          : "Continuar",

        variant: "contained",
        onClick: () =>
          activeStep === 3 ? formikRef.current?.handleSubmit() : handleGoNext(),
      },
    ];
  }, [activeStep, formikRef, updateForm.isLoading, handleGoNext, handleGoBack]);

  const renderContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <Infos />;
      case 1:
        return <Fields />;
      case 2:
        return <Colors />;
      case 3:
        return <Review />;
      default:
        return <></>;
    }
  }, [activeStep]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      title={data?.nameForm}
      open={open}
      actions={actions}
      onClose={handleClose}
    >
      <Stepper activeStep={activeStep}>
        {map(steps, (step) => {
          return (
            <Step key={step}>
              <StepLabel>{step}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <div className={styles.rootContent}>
        <Formik
          innerRef={formikRef}
          initialValues={
            {
              fields: fieldsForm,
              bgColorForm: data?.bgColorForm,
              fontColorForm: data?.fontColorForm,
              typeForm: data?.typeForm,
              nameForm: data?.nameForm,
            } as CreateFormData
          }
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleUpdate}
        >
          {renderContent()}
        </Formik>
      </div>
    </Dialog>
  );
}
